import {
  Component,
  OnDestroy,
  OnChanges,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  ChangeDetectorRef,
  Input,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AiAssistantService,
  AiChatMessage,
  PatientContext,
} from '../../../core/services/ai/ai-assistant.service';
import { FormatAiTextPipe } from '../../../pipes/format-ai-text.pipe';
import { PatientDetailResponse } from '../../../core/models/patient/patient-detail.model';
import {
  GetWoundByIdResponse,
  WoundAnalysisMetrics,
} from '../../../core/models/wound/wound.model';

interface UiMessage {
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
  isError?: boolean;
  timestamp: Date;
}

@Component({
  selector: 'app-ai-assistant',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, FormatAiTextPipe],
  templateUrl: './ai-assistant.component.html',
  styleUrl: './ai-assistant.component.scss',
})
export class AiAssistantComponent
  implements OnDestroy, AfterViewChecked, OnChanges
{
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('textInput') private textInput!: ElementRef;

  /** Pass patient + wound when embedded inside worklist-detail */
  @Input() patient: PatientDetailResponse | null = null;
  @Input() wound: GetWoundByIdResponse | null = null;
  @Input() woundMetrics: WoundAnalysisMetrics | null = null;

  messages: UiMessage[] = [];
  inputText = '';
  isStreaming = false;
  copiedIndex: number | null = null;
  private abortController: AbortController | null = null;
  private shouldScroll = false;
  private copyTimeout: ReturnType<typeof setTimeout> | null = null;

  patientContext: PatientContext | null = null;

  readonly suggestionKeys = [
    'aiAssistant.suggestions.q1',
    'aiAssistant.suggestions.q2',
    'aiAssistant.suggestions.q3',
    'aiAssistant.suggestions.q4',
    'aiAssistant.suggestions.q5',
  ];

  constructor(
    private aiService: AiAssistantService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['patient'] || changes['wound'] || changes['woundMetrics']) {
      this.buildContextFromInputs();
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  ngOnDestroy(): void {
    this.abortController?.abort();
    if (this.copyTimeout) clearTimeout(this.copyTimeout);
  }

  private buildContextFromInputs(): void {
    if (!this.patient && !this.wound) {
      return;
    }

    const p = this.patient as any;
    const w = this.wound as any;
    const m = this.woundMetrics;

    const ctx: PatientContext = {};

    if (p) {
      const name = [p.firstName, p.lastName].filter(Boolean).join(' ');
      if (name) ctx.patientName = name;
      if (p.birthDate || p.age)
        ctx.patientAge = p.age?.toString() ?? p.birthDate ?? undefined;
    }

    if (w) {
      ctx.woundType = this.resolveWoundType(w.woundType, w.woundCategoryType);
      ctx.woundLocation = w.name ?? w.description ?? undefined;
      ctx.woundStage = this.resolveWoundStatus(w.woundStatus);
      if (w.treatmentPlannedDate)
        ctx.lastAssessmentDate = new Date(
          w.treatmentPlannedDate
        ).toLocaleDateString('tr-TR');
      if (w.doctorAnswer?.answer) ctx.treatmentPlan = w.doctorAnswer.answer;
    }

    if (m) {
      const area = m.surfaceAreaCm2?.toFixed(2);
      const vol = m.volumeCm3?.toFixed(3);
      const parts: string[] = [];
      if (area) parts.push(`Alan: ${area} cm²`);
      if (vol) parts.push(`Hacim: ${vol} cm³`);
      if (m.boundaryLengthMm)
        parts.push(`Çevre: ${(m.boundaryLengthMm / 10).toFixed(1)} cm`);
      if (parts.length) ctx.woundSize = parts.join(', ');
    }

    this.patientContext = { ...ctx };
  }

  private resolveWoundType(woundType: number, category: number): string {
    const types: Record<number, string> = {
      0: 'Belirtilmemiş',
      1: 'Basınç yarası',
      2: 'Diyabetik ülser',
      3: 'Venöz ülser',
      4: 'Arteriyel ülser',
      5: 'Cerrahi yara',
      6: 'Travmatik yara',
    };
    return types[woundType] ?? `Tip ${woundType}`;
  }

  private resolveWoundStatus(status: number): string {
    const statuses: Record<number, string> = {
      0: 'Değerlendirilmedi',
      1: 'Aktif',
      2: 'İyileşiyor',
      3: 'İyileşti',
      4: 'Kronik',
    };
    return statuses[status] ?? `Durum ${status}`;
  }

  send(): void {
    const text = this.inputText.trim();
    if (!text || this.isStreaming) return;

    this.messages.push({ role: 'user', content: text, timestamp: new Date() });
    this.inputText = '';
    this.shouldScroll = true;
    this.autoResizeInput();

    const assistantMsg: UiMessage = {
      role: 'assistant',
      content: '',
      streaming: true,
      timestamp: new Date(),
    };
    this.messages.push(assistantMsg);
    this.isStreaming = true;
    this.shouldScroll = true;

    const history: AiChatMessage[] = this.messages
      .slice(0, -1)
      .filter((m) => m.content.trim().length > 0 && !m.isError)
      .map((m) => ({ role: m.role, content: m.content }));

    this.abortController = new AbortController();

    this.aiService.streamChat(
      history,
      this.patientContext,
      this.getAutoImageUrls(),
      (chunk) => {
        assistantMsg.content += chunk;
        this.shouldScroll = true;
        this.cdr.detectChanges();
      },
      () => {
        if (!assistantMsg.content.trim()) {
          assistantMsg.content = this.translate.instant('aiAssistant.error');
          assistantMsg.isError = true;
        }
        assistantMsg.streaming = false;
        this.isStreaming = false;
        this.cdr.detectChanges();
      },
      (err) => {
        console.error('[AI Assistant] Stream error:', err);
        console.error('[AI Assistant] Error name:', err?.name);
        console.error('[AI Assistant] Error message:', err?.message);
        console.error('[AI Assistant] Error stack:', err?.stack);
        assistantMsg.content = this.translate.instant('aiAssistant.error');
        assistantMsg.streaming = false;
        assistantMsg.isError = true;
        this.isStreaming = false;
        this.cdr.detectChanges();
      },
      this.abortController.signal
    );
  }

  private getAutoImageUrls(): string[] {
    const rawUrls: Array<string | null | undefined> = [
      this.wound?.imagePathOne?.path,
      this.wound?.imagePathTwo?.path,
      this.wound?.imagePathThree?.path,
      this.woundMetrics?.steepestViewOriginalPath,
      this.woundMetrics?.steepestViewSegmentedPath,
    ];

    const normalized = rawUrls
      .filter((u): u is string => !!u && !!u.trim())
      .map((u) => this.toAbsoluteUrl(u.trim()))
      .filter((u): u is string => !!u);

    return [...new Set(normalized)];
  }

  private toAbsoluteUrl(rawUrl: string): string | null {
    if (/^https?:\/\//i.test(rawUrl)) {
      return rawUrl;
    }

    if (rawUrl.startsWith('/')) {
      return `${window.location.origin}${rawUrl}`;
    }

    return null;
  }

  stopStreaming(): void {
    this.abortController?.abort();
    const last = this.messages[this.messages.length - 1];
    if (last?.streaming) {
      last.streaming = false;
      // If nothing was received yet, remove the empty placeholder so it
      // doesn't end up as an empty assistant turn in the next request's history.
      if (!last.content.trim()) {
        this.messages.pop();
      }
    }
    this.isStreaming = false;
  }

  useSuggestion(key: string): void {
    this.inputText = this.translate.instant(key);
    this.send();
  }

  copyMessage(content: string, index: number): void {
    const plain = content.replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    navigator.clipboard.writeText(plain).then(() => {
      this.copiedIndex = index;
      if (this.copyTimeout) clearTimeout(this.copyTimeout);
      this.copyTimeout = setTimeout(() => {
        this.copiedIndex = null;
        this.cdr.detectChanges();
      }, 2000);
      this.cdr.detectChanges();
    });
  }

  clearChat(): void {
    if (this.isStreaming) this.stopStreaming();
    this.messages = [];
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  autoResizeInput(): void {
    const el = this.textInput?.nativeElement as HTMLTextAreaElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }

  private scrollToBottom(): void {
    try {
      const el = this.messagesContainer?.nativeElement as HTMLElement;
      if (el) el.scrollTop = el.scrollHeight;
    } catch {}
  }

  trackByIndex(index: number): number {
    return index;
  }
}
